import { GridHeaderSelectionChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useShoppingListProductColumns from 'src/assets/static/shopping_lists/useShoppingListProductColumns';
import GridWrapper from 'src/components/GridWrapper';
import { ShoppingListProduct } from 'src/types/shopping_lists/ShoppingList';
import { useContextSelector } from 'use-context-selector';
import ShoppingListProductInformationPanel from './ShoppingListProductInformationPanel';
import ShoppingListProductToolbarActions from './ShoppingListProductToolbarActions';
import { ShoppingListProductsContext } from './ShoppingListProductsContext';
import { useShoppingListReorderRowRender } from './useShoppingListReorderRowRender';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const defaultProps = {
  tableTextId: 'Products',
  DataItemKey: 'productKey',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable,
  pageable: gridPagerSettings,
} as const;

const ShoppingListProductListView: FC = () => {
  const { t } = useTranslation();

  const listId = useContextSelector(ShoppingListProductsContext, (state) => state.listId);
  const dataState = useContextSelector(ShoppingListProductsContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.onDataStateChange
  );
  const onSelectionChange = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.onSelectionChange
  );
  const onItemChange = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.onItemChange
  );
  const productSelections = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.productSelections
  );
  const setProductSelections = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.setProductSelections
  );
  const productsData = useContextSelector(ShoppingListProductsContext, (state) => state.products);
  const productsTotal = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.productsTotal
  );
  const loadingProducts = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.loadingProducts
  );
  const filterSearch = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.filterSearch
  );
  const isProductPanelOpen = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.handleCloseProductPanel
  );

  const catalogData = useMemo(() => {
    if (!productsData.length) {
      return [];
    }

    return productsData.map((item: ShoppingListProduct) => {
      const selection = productSelections[item.productKey] || { selected: false, qty: 0 };
      return {
        ...item,
        selected: selection.selected,
        qty: selection.qty,
      };
    });
  }, [productsData, productSelections]);

  const columns = useShoppingListProductColumns();
  const rowRender = useShoppingListReorderRowRender();

  const handleHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event?.syntheticEvent?.target as HTMLInputElement;
      const { checked } = checkboxElement;

      setProductSelections((prev) => {
        const updated = { ...prev };
        catalogData.forEach((item) => {
          const { productKey } = item;
          const current = prev[productKey] || { selected: false, qty: 0 };
          updated[productKey] = {
            selected: checked,
            qty: checked && current.qty === 0 ? 1 : checked ? current.qty : 0,
          };
        });
        return updated;
      });
    },
    [catalogData, setProductSelections]
  );

  const hasFilterSearchValue = Boolean(filterSearch.trim());

  return (
    <main className="flex h-full min-h-0 min-w-0 flex-col px-4 pb-4">
      {hasFilterSearchValue && (
        <div className="mb-2 shrink-0 truncate text-sm font-[600]">
          {t('Showing results for')} &quot;{filterSearch.trim()}&quot;
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <GridWrapper
          key={`shopping-list-grid-${listId}`}
          id="shoppingListProductListTable"
          className="product-list-view h-full min-h-0"
          columns={columns}
          dataSource={catalogData}
          total={productsTotal}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          height="100%"
          gridToolbarChildren={<ShoppingListProductToolbarActions />}
          {...defaultProps}
          loading={loadingProducts}
          noRecordText={t('No items to display')}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          onSelectionChange={onSelectionChange}
          onItemChange={onItemChange}
          rowRender={rowRender}
          tableTextId="Items"
        />
      </div>
      <ShoppingListProductInformationPanel
        isOpen={isProductPanelOpen}
        onClose={handleCloseProductPanel}
      />
    </main>
  );
};

export default ShoppingListProductListView;
